/**
 * The omnibar's conversation lives outside React, at module scope.
 *
 * The bar sits in the AngularJS navbar, which is inside the `app` state's template: navigating
 * re-links the navbar and mounts a fresh React root. Keeping the entries and the mode here means
 * a plan that navigates can still report what it did afterwards, instead of the panel vanishing
 * with the old component.
 */

import { AiCapabilityResultRow } from "./ai-capability.types";

export type AiOmnibarStepStatus = "running" | "done" | "failed";

/** An entry before the store gives it an id. */
export type AiOmnibarEntryDraft =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string }
  | { kind: "note"; text: string }
  | { kind: "step"; text: string; status: AiOmnibarStepStatus }
  | { kind: "results"; text: string; rows: AiCapabilityResultRow[] };

export type AiOmnibarEntry = AiOmnibarEntryDraft & { id: number };

export type AiOmnibarMode = "search" | "ai";

/** One side of the conversation as the model sees it: what was asked, and what it answered. */
export type AiOmnibarContextMessage = { role: "user" | "assistant"; text: string };

let entries: AiOmnibarEntry[] = [];
let mode: AiOmnibarMode = "search";
let nextId = 0;

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribeToAiOmnibarEntries(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getAiOmnibarEntries(): AiOmnibarEntry[] {
  return entries;
}

export function appendAiOmnibarEntry(entry: AiOmnibarEntryDraft): number {
  const id = nextId++;

  entries = [...entries, { ...entry, id }];
  notify();

  return id;
}

/**
 * Steps are the only entries that change after being added: they start out running and end up done
 * or failed, sometimes with the line rewritten by the capability that ran.
 */
export function updateAiOmnibarStep(
  id: number,
  patch: { status: AiOmnibarStepStatus; text?: string }
): void {
  entries = entries.map((entry) =>
    entry.id === id && entry.kind === "step"
      ? {
          ...entry,
          status: patch.status,
          text: patch.text ?? entry.text,
        }
      : entry
  );
  notify();
}

export function clearAiOmnibarEntries(): void {
  entries = [];
  notify();
}

/**
 * What the model is told about the conversation so far.
 *
 * Not the whole history: only the run of turns since the last request the model judged unrelated.
 * It reports that as `continuesPrevious`, and a false answer starts the window again - so a finished
 * task stops colouring the next one, while "phone number 2127184131" still knows what it follows.
 *
 * Only the request and the model's own reply go in. Step lines and results are UI detail; sending
 * them would spend context on text the model did not write and cannot act on.
 */

let contextWindow: AiOmnibarContextMessage[] = [];

export function getAiOmnibarContext(): AiOmnibarContextMessage[] {
  return contextWindow;
}

/**
 * Records a completed turn. `continuesPrevious` false means this request opened a new subject, so
 * the window becomes just this turn.
 */
export function recordAiOmnibarTurn(params: {
  request: string;
  reply: string;
  continuesPrevious: boolean;
}): void {
  const turn: AiOmnibarContextMessage[] = [
    { role: "user", text: params.request },
    { role: "assistant", text: params.reply },
  ];

  contextWindow = params.continuesPrevious ? [...contextWindow, ...turn] : turn;
}

export function clearAiOmnibarContext(): void {
  contextWindow = [];
}

/**
 * Which model plans the request, for trying alternatives in a dev slot.
 *
 * Set `localStorage["medflyt:ai-omnibar-provider"]` to "bedrock-deepseek" or "bedrock-sonnet" to
 * route planning through Bedrock instead of Gemini; anything else (or absent) leaves the server's
 * default alone. Deliberately
 * not a UI control - picking the model is a deployment decision, not something an agency member
 * should be choosing per request.
 */
export type AiOmnibarProviderOverride =
  | "gemini"
  | "bedrock-deepseek"
  | "bedrock-sonnet"
  | "bedrock-gpt";

export function getAiOmnibarProviderOverride(): AiOmnibarProviderOverride | undefined {
  try {
    const stored = window.localStorage.getItem("medflyt:ai-omnibar-provider");

    const known: AiOmnibarProviderOverride[] = [
      "gemini",
      "bedrock-deepseek",
      "bedrock-sonnet",
      "bedrock-gpt",
    ];

    return known.find((provider) => provider === stored);
  } catch {
    return undefined;
  }
}

/**
 * Which half of the bar is showing. AngularJS owns the visible state (it holds the `ai-mode`
 * class); this is only so the choice survives a navbar re-link.
 */
export function getAiOmnibarMode(): AiOmnibarMode {
  return mode;
}

export function setAiOmnibarMode(next: AiOmnibarMode): void {
  mode = next;
}
